/**
 * Beat 1's voices: her sibling and her mother (D-250).
 *
 * Until session 91 the game had no pig voice nearer than beat 10's herd, and
 * the sow "answered" a nudge with Rosalie's own breath pitched down. Alex's
 * report on beat 1 was that nothing in the pen felt alive and the taking read
 * as a despawn; a mother who grunts back and a sibling who squeaks when woken
 * are what turn the empty straw at the end into the loss of somebody.
 *
 * Three sounds and no more. The sibling's squeal is the only bright, harsh
 * sound in Act 1 before the cull corridor, and it has to stay that way: it is
 * the one moment the beat is allowed to be loud.
 */

import { connectChain, filter, gain, lfo, lfoOut, noiseSource, softClip } from '../synth';
import type { SoundDefinition } from '../types';

/** A pitch contour: `[fraction of the voice's duration, Hz]`, in order. */
type Contour = readonly (readonly [number, number])[];

/** One band of the vocal tract: centre Hz, Q, and relative level. */
type Formant = readonly [number, number, number];

interface VoiceSpec {
  readonly at: number;
  readonly duration: number;
  readonly contour: Contour;
  readonly formants: readonly Formant[];
  /** Peak level of the voiced body. */
  readonly level: number;
  /** Seconds to full level. A squeal slams open; a grunt swells. */
  readonly attack: number;
  /** Amplitude-roughness rate, Hz. A pig's voice is pulsed, not smooth. */
  readonly roughHz: number;
  readonly roughDepth: number;
  /** Level of the breath/snort noise under the voice, relative to `level`. */
  readonly breath: number;
  readonly breathBand: number;
  /** Everything above this is removed; it is what keeps a grunt from rasping. */
  readonly ceiling: number;
  readonly seed: number;
}

/** Schedule one vocalisation into `out`. */
function voice(ctx: BaseAudioContext, out: AudioNode, spec: VoiceSpec): void {
  const t0 = ctx.currentTime + spec.at;
  const end = t0 + spec.duration;

  // The pulse: a gain resting at 1 − depth/2 and swung ±depth/2 by an LFO, so
  // the voice is always sounding and never gated to silence mid-call.
  const shape = gain(ctx, 0);
  const rough = gain(ctx, 1 - spec.roughDepth / 2);
  lfoOut(lfo(ctx, spec.roughHz, spec.roughDepth / 2, 'triangle')).connect(rough.gain);
  connectChain(shape, rough, filter(ctx, { type: 'lowpass', frequency: spec.ceiling, q: 0.7 }), out);

  // A sawtooth for the glottal buzz every one of these has; a sine through the
  // same formants is a whistle, and nothing in a pen whistles.
  const source = ctx.createOscillator();
  source.type = 'sawtooth';
  for (const [fraction, hz] of spec.contour) {
    const at = t0 + fraction * spec.duration;
    if (fraction === 0) source.frequency.setValueAtTime(hz, at);
    else source.frequency.linearRampToValueAtTime(hz, at);
  }
  source.start(t0);
  source.stop(end);

  // Parallel formants rather than one filter in series: series bandpasses
  // multiply their slopes and leave a thin whistle, which is the bird this
  // must not sound like.
  for (const [hz, q, level] of spec.formants) {
    connectChain(source, filter(ctx, { type: 'bandpass', frequency: hz, q }), gain(ctx, level), shape);
  }

  shape.gain.setValueAtTime(0, t0);
  shape.gain.linearRampToValueAtTime(spec.level, t0 + spec.attack);
  shape.gain.setValueAtTime(spec.level, Math.max(t0 + spec.attack, end - spec.duration * 0.35));
  shape.gain.exponentialRampToValueAtTime(0.0001, end);
  shape.gain.setValueAtTime(0, end + 0.001);

  // Air at the mouth: a snort under a grunt, a rasp under a squeal.
  const breath = gain(ctx, 0);
  connectChain(
    noiseSource(ctx, spec.duration + 0.1, spec.seed),
    filter(ctx, { type: 'bandpass', frequency: spec.breathBand, q: 0.9 }),
    filter(ctx, { type: 'lowpass', frequency: spec.ceiling, q: 0.7 }),
    breath,
    out,
  );
  const breathPeak = spec.level * spec.breath;
  breath.gain.setValueAtTime(0, t0);
  breath.gain.linearRampToValueAtTime(breathPeak, t0 + Math.min(0.02, spec.attack));
  breath.gain.exponentialRampToValueAtTime(Math.max(0.0001, breathPeak * 0.25), t0 + spec.duration * 0.5);
  breath.gain.exponentialRampToValueAtTime(0.0001, end);
  breath.gain.setValueAtTime(0, end + 0.001);
}

/**
 * Her mother's answer: two low, closed-mouth grunts, the second lower.
 *
 * A sow's contact grunt to her litter is short, soft and repeated; she is not
 * calling anybody, she is saying she is there. The two pulses are the whole of
 * it — a third turns the answer into a rhythm, and a rhythm in a room full of
 * machines reads as one more machine.
 */
const sowGrunt: SoundDefinition = {
  id: 'sow-grunt',
  label: 'Sow contact grunt',
  description: 'Two low, nasal, closed-mouth grunts from a sow lying in a crate; the second falls away.',
  category: 'creature',
  duration: 1.0,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 1.6);
    connectChain(out, ctx.destination);
    const grunt = (at: number, duration: number, f0: number, seed: number): void => {
      voice(ctx, out, {
        at,
        duration,
        contour: [[0, f0 * 0.92], [0.3, f0 * 1.06], [1, f0 * 0.78]],
        formants: [[320, 2.4, 0.9], [760, 3, 0.35], [1350, 4, 0.08]],
        level: 0.42,
        attack: 0.045,
        roughHz: 23,
        roughDepth: 0.55,
        breath: 0.35,
        breathBand: 420,
        ceiling: 1600,
        seed,
      });
    };
    grunt(0.02, 0.26, 92, 20_101);
    grunt(0.44, 0.32, 84, 20_102);
  },
};

/**
 * Her sibling, woken: one short, bright, upward squeak.
 *
 * The contact call a piglet makes to a littermate, not a cry. It rises, which
 * is what makes it a question addressed to somebody, and it is short enough
 * that it can answer every nudge without becoming a noise the player tunes out.
 */
const pigletSqueak: SoundDefinition = {
  id: 'piglet-squeak',
  label: 'Piglet squeak',
  description: 'One short rising contact squeak from a three-day-old piglet.',
  category: 'creature',
  duration: 0.3,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 2.0);
    connectChain(out, ctx.destination);
    voice(ctx, out, {
      at: 0.01,
      duration: 0.2,
      contour: [[0, 380], [0.55, 560], [1, 470]],
      formants: [[1100, 3, 0.8], [2400, 4, 0.4], [3600, 6, 0.1]],
      level: 0.3,
      attack: 0.02,
      roughHz: 41,
      roughDepth: 0.35,
      breath: 0.2,
      breathBand: 1800,
      ceiling: 4800,
      seed: 20_201,
    });
  },
};

/**
 * Her sibling, lifted: the distress squeal.
 *
 * Piglets scream when they are picked up. It is the loudest sound an animal
 * that size makes and it is not cute, so this is built to be unpleasant: a
 * sawtooth pushed high, two bright formants, a fast pulse and a saturator for
 * the rasp. One call, 0.8 s, with a catch in the middle where the breath runs
 * out; the director plays it more than once and further away each time.
 */
const pigletSqueal: SoundDefinition = {
  id: 'piglet-squeal',
  label: 'Piglet distress squeal',
  description: 'A harsh, high piglet scream with a breath-catch, as when lifted by a person.',
  category: 'creature',
  duration: 0.95,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.5);
    // Saturated AFTER the voice, into a fixed ceiling: the rasp is the
    // distortion, and the gate's peak limit is what the output gain is for.
    connectChain(out, softClip(ctx, 2.6), gain(ctx, 0.62), ctx.destination);
    voice(ctx, out, {
      at: 0.005,
      duration: 0.42,
      contour: [[0, 820], [0.2, 1250], [0.8, 1180], [1, 900]],
      formants: [[1700, 2.5, 0.9], [3100, 3.5, 0.55], [4700, 5, 0.18]],
      level: 0.8,
      attack: 0.012,
      roughHz: 57,
      roughDepth: 0.5,
      breath: 0.3,
      breathBand: 2600,
      ceiling: 7000,
      seed: 20_301,
    });
    voice(ctx, out, {
      at: 0.47,
      duration: 0.44,
      contour: [[0, 1050], [0.25, 1320], [1, 760]],
      formants: [[1700, 2.5, 0.9], [3100, 3.5, 0.55], [4700, 5, 0.18]],
      level: 0.72,
      attack: 0.01,
      roughHz: 63,
      roughDepth: 0.55,
      breath: 0.35,
      breathBand: 2600,
      ceiling: 7000,
      seed: 20_302,
    });
  },
};

export const pigSounds: readonly SoundDefinition[] = [sowGrunt, pigletSqueak, pigletSqueal];
